import React from 'react';
import {
    FireExtinguisher, AlertTriangle, CheckCircle,
    Calendar, MapPin, Edit2, Trash2, Info
} from 'lucide-react';

interface Equipment {
    id: string;
    equipmentNumber: string;
    equipmentType: 'WATER' | 'FOAM' | 'ABC_POWDER' | 'BC_POWDER' | 'CO2' | 'WET_CHEMICAL' | 'CLASS_D';
    capacity: string;
    status: 'ACTIVE' | 'MAINTENANCE' | 'RETIRED';
    locationDescription?: string;
    installationDate: number;
    ratingClassA?: string;
    ratingClassB?: string;
}

interface EquipmentCardProps {
    item: Equipment;
    onEdit: (item: Equipment) => void;
    onDelete: (id: string) => void;
}

const getTypeColor = (type: string) => {
    switch (type) {
        case 'WATER': return 'bg-blue-500';
        case 'FOAM': return 'bg-yellow-100 border-yellow-500'; // Cream/Yellowish
        case 'ABC_POWDER': return 'bg-blue-600'; // Usually Blue or Multi
        case 'BC_POWDER': return 'bg-blue-600';
        case 'CO2': return 'bg-gray-800'; // Black/Grey
        case 'WET_CHEMICAL': return 'bg-yellow-400';
        default: return 'bg-red-600';
    }
};

const getStatusBadge = (status: string) => {
    switch (status) {
        case 'ACTIVE':
            return <span className="badge badge-success flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Active</span>;
        case 'MAINTENANCE':
            return <span className="badge badge-warning flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Maintenance</span>;
        case 'RETIRED':
            return <span className="badge badge-danger flex items-center gap-1"><Info className="w-3 h-3" /> Retired</span>;
        default: return null;
    }
};

export default function EquipmentCard({ item, onEdit, onDelete }: EquipmentCardProps) {
    return (
        <div className="card-glass p-5 hover:shadow-lg transition-all duration-300 group animate-fadeIn">
            <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-md ${getTypeColor(item.equipmentType)}`}>
                        <FireExtinguisher className="w-6 h-6" />
                    </div>
                    <div>
                        <h4 className="font-display font-bold text-white text-lg line-clamp-1">
                            {item.equipmentType.replace('_', ' ')}
                        </h4>
                        <p className="text-steel-400 text-xs font-mono">
                            #{item.equipmentNumber}
                        </p>
                    </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                    {getStatusBadge(item.status)}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-4 text-sm">
                <div className="bg-steel-800/30 p-2 rounded border border-steel-700/30">
                    <p className="text-steel-500 text-xs mb-0.5">Capacity</p>
                    <p className="text-white font-medium">{item.capacity}</p>
                </div>
                <div className="bg-steel-800/30 p-2 rounded border border-steel-700/30">
                    <p className="text-steel-500 text-xs mb-0.5">Installed</p>
                    <p className="text-white font-medium flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-steel-400" />
                        {new Date(item.installationDate).toLocaleDateString()}
                    </p>
                </div>
            </div>

            {item.locationDescription && (
                <div className="flex items-start gap-2 text-steel-400 text-xs mb-4 bg-steel-800/20 p-2 rounded">
                    <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                    <span className="line-clamp-2">{item.locationDescription}</span>
                </div>
            )}

            <div className="flex items-center gap-2 mt-auto pt-3 border-t border-steel-700/30 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                    onClick={() => onEdit(item)}
                    className="flex-1 btn btn-xs btn-ghost hover:bg-ember-500/10 hover:text-ember-400"
                >
                    <Edit2 className="w-3 h-3 mr-1" /> Edit
                </button>
                <div className="w-px h-4 bg-steel-700"></div>
                <button
                    onClick={() => onDelete(item.id)}
                    className="flex-1 btn btn-xs btn-ghost hover:bg-red-500/10 hover:text-red-400"
                >
                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                </button>
            </div>
        </div>
    );
}
